import { useCallback, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { uploadToS3 } from './upload';
import {
  FilePurpose,
  isAllowedImageType,
  maxSizeForPurpose,
  resolveUploadMime,
} from './constants';
import type {
  ConfirmUploadRequest,
  MediaResponse,
  PresignedUploadRequest,
  PresignedUploadResponse,
  UploadResult,
} from './media.type.ts';

interface UseUploadMediaOptions<TMedia = MediaResponse> {
  /**
   * presigned 발급. 앱이 자기 apiClient로 구현해 주입.
   * (Gateway → media-service. S3 자격증명은 media-service에만)
   */
  getPresignedUrl: (
    req: PresignedUploadRequest,
  ) => Promise<PresignedUploadResponse>;
  /**
   * 업로드 확정. 주입하면 PUT 직후 호출해 MediaResponse를 받는다.
   * 생략 시 s3Key만 반환 — key를 엔티티 생성 요청에 실어 서버에서 확정하는
   * 패턴(게시글 저장 시 함께 confirm)을 쓸 때.
   */
  confirmUpload?: (req: ConfirmUploadRequest) => Promise<TMedia>;
  /** purpose 미지정 시 기본값 */
  defaultPurpose?: FilePurpose;
  /**
   * 허용 MIME 검증 함수.
   * 기본값은 이미지 전용(isAllowedImageType) — 기존 사용처 하위 호환.
   * 문서 첨부까지 허용하려면 isAllowedUploadType 을 주입한다.
   */
  validateType?: (mime: string) => boolean;
}

interface UploadArgs {
  file: File;
  purpose?: FilePurpose;
}

/**
 * presigned → S3 PUT → (옵션) confirm 을 한 번에 처리하는 업로드 훅.
 * 진행률/취소 제공. CRUD는 useMutation이므로 컴포넌트에서 axios 직접 호출 금지.
 *
 * 검증 정책은 validateType 으로 주입한다(기본: 이미지 전용).
 * 문서 첨부는 useUploadMedia({ validateType: isAllowedUploadType, defaultPurpose: FilePurpose.DOCUMENT }).
 */
export function useUploadMedia<TMedia = MediaResponse>({
  getPresignedUrl,
  confirmUpload,
  defaultPurpose = FilePurpose.OTHER,
  validateType = isAllowedImageType,
}: UseUploadMediaOptions<TMedia>) {
  const [progress, setProgress] = useState(0);
  const abortRef = useRef<AbortController | null>(null);

  const mutation = useMutation<UploadResult<TMedia>, Error, UploadArgs>({
    mutationFn: async ({ file, purpose = defaultPurpose }) => {
      setProgress(0);
      const mimeType = resolveUploadMime(file);

      // 클라 선검증 (최종 신뢰 경계는 백엔드)
      if (!validateType(mimeType)) {
        throw new Error(`[media] 허용되지 않은 형식: ${mimeType || 'unknown'}`);
      }
      if (file.size > maxSizeForPurpose(purpose)) {
        throw new Error('[media] 파일 크기 초과');
      }

      const controller = new AbortController();
      abortRef.current = controller;

      const { uploadUrl, s3Key } = await getPresignedUrl({
        fileName: file.name,
        mimeType,
        fileSize: file.size,
        purpose,
      });

      await uploadToS3({
        uploadUrl,
        file,
        contentType: mimeType,
        onProgress: setProgress,
        signal: controller.signal,
      });

      const media = confirmUpload ? await confirmUpload({ s3Key }) : null;
      return { s3Key, media, mimeType };
    },
    onSettled: () => {
      abortRef.current = null;
    },
  });

  const cancel = useCallback(() => abortRef.current?.abort(), []);

  const reset = useCallback(() => {
    setProgress(0);
    mutation.reset();
  }, [mutation]);

  return {
    /** (args) => Promise<UploadResult> */
    upload: mutation.mutateAsync,
    result: mutation.data ?? null,
    isUploading: mutation.isPending,
    error: mutation.error,
    progress,
    cancel,
    reset,
  };
}
